import { useEffect, type ReactNode } from 'react'
import { BrowserRouter, MemoryRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { usePageContext } from 'vike-react/usePageContext'
import { AuthProvider } from '../../context/AuthContext'
import { CartProvider } from '../../context/CartContext'
import { WishlistProvider } from '../../context/WishlistContext'
import { ConsentProvider } from '../../context/ConsentContext'
import SiteHeader from '../../components/layout/SiteHeader'
import SiteFooter from '../../components/layout/SiteFooter'
import CookieBanner from '../../components/layout/CookieBanner'
import TopPromiseBar from '../../components/layout/TopPromiseBar'
import i18n, { supportedLanguages } from '../../i18n'

// A single QueryClient lives on the client for the whole site. Each SSR
// render gets its own so cross-request cache bleed is impossible.
let browserClient: QueryClient | null = null
function getQueryClient(): QueryClient {
  if (typeof window === 'undefined') {
    return new QueryClient({
      defaultOptions: { queries: { staleTime: 0, retry: 0 } },
    })
  }
  if (!browserClient) {
    browserClient = new QueryClient({
      defaultOptions: { queries: { staleTime: 60_000, retry: 1 } },
    })
  }
  return browserClient
}

/**
 * SSR-safe Router wrapper. On the server we use MemoryRouter so react-router-dom
 * hooks (`useLocation`, `useParams`, `useNavigate`, `<Link>`) don't crash during
 * renderToString. On the client we use BrowserRouter so those hooks behave as
 * they always have.
 */
function AppRouter({ urlPathname, children }: { urlPathname: string; children: ReactNode }) {
  if (typeof window === 'undefined') {
    return <MemoryRouter initialEntries={[urlPathname]}>{children}</MemoryRouter>
  }
  return <BrowserRouter>{children}</BrowserRouter>
}

/**
 * Intercept react-router-dom Link clicks. Links call `history.pushState`
 * directly. Only the current Vike page is mounted on this SSR shell — there
 * is no whole-site Routes table — so pushing without navigating would leave
 * the user staring at stale content. We redirect such pushState calls to a
 * full `window.location.assign` so Vike renders the real target page (SSR
 * for the pages we SSR, SPA-shell catch-all for everything else).
 */
function NavigationInterceptor() {
  useEffect(() => {
    if (typeof window === 'undefined') return

    const origPushState = window.history.pushState.bind(window.history)

    function patchedPushState(data: unknown, unused: string, url?: string | URL | null) {
      if (url != null) {
        const target = typeof url === 'string' ? url : url.toString()
        const resolved = new URL(target, window.location.href)
        if (resolved.origin === window.location.origin) {
          window.location.assign(resolved.href)
          return
        }
      }
      origPushState(data, unused, url ?? null)
    }
    window.history.pushState = patchedPushState as typeof window.history.pushState

    function onPopState() {
      // Back/forward may have moved to a different Vike page. Hard reload.
      window.location.reload()
    }
    window.addEventListener('popstate', onPopState)
    return () => {
      window.history.pushState = origPushState
      window.removeEventListener('popstate', onPopState)
    }
  }, [])
  return null
}

export interface ShopChromeLayoutProps {
  children: ReactNode
}

/**
 * The full shop chrome — same header/footer/cart/providers the CSR pages
 * use, but wired so it works under SSR. Used by every Vike SSR page that
 * should look like the rest of the shop.
 */
export default function ShopChromeLayout({ children }: ShopChromeLayoutProps) {
  const pageContext = usePageContext()
  const urlPathname = (pageContext.urlPathname as string | undefined) ?? '/'
  const client = getQueryClient()

  // Sync i18n language from URL path segment (e.g. /nl/product/... → 'nl').
  // On the server, useEffect is skipped, so we do the sync synchronously here
  // so SSR-rendered strings are in the correct language.
  const urlLang = urlPathname.split('/')[1]
  const resolvedLang = (supportedLanguages as readonly string[]).includes(urlLang) ? urlLang : 'nl'
  if (i18n.language !== resolvedLang) {
    i18n.changeLanguage(resolvedLang)
  }
  useEffect(() => {
    if (i18n.language !== resolvedLang) {
      i18n.changeLanguage(resolvedLang)
    }
  }, [resolvedLang])

  return (
    <AppRouter urlPathname={urlPathname}>
      <QueryClientProvider client={client}>
        <AuthProvider>
          <ConsentProvider>
          <CartProvider>
          <WishlistProvider>
            <NavigationInterceptor />
            <div className="min-h-screen flex flex-col bg-neutral-50">
              <TopPromiseBar />
              <SiteHeader />
              {/* Reserve a viewport-height minimum for <main> so the footer settles below the
                  fold from the first paint, regardless of how slowly the page content hydrates.
                  Without this, the empty SSR shell paints with the footer right under the header
                  and shifts down by ~1500px once the page mounts — a CLS catastrophe. */}
              <main className="flex-1 min-h-[80vh]">{children}</main>
              <SiteFooter />
              <CookieBanner />
            </div>
            <Toaster position="top-right" richColors />
          </WishlistProvider>
          </CartProvider>
          </ConsentProvider>
        </AuthProvider>
      </QueryClientProvider>
    </AppRouter>
  )
}
